import React, { useCallback, useEffect, useRef, useState } from 'react';
import {FaceLandmarker, FilesetResolver, Landmark, PoseLandmarker} from "@mediapipe/tasks-vision";
import { VideoTexture } from "three";
import { Canvas } from '@react-three/fiber';
import { Environment, OrthographicCamera } from '@react-three/drei';
import EarringMesh from './EarringMesh';
import NecklaceMesh from './NecklaceMesh';
import { transformfaceLandmarks, transformPoseLandmarks } from './utils';

const EARRING_MODEL_PATH = "/models/pearl_earring.glb";
const NECKLACE_MODEL_PATH = "/models/necklace_b.glb";
const EARRING_IMAGE_PATH = "/models/Earring-5.png";
const NECKLACE_IMAGE_PATH = "/models/Necklace-1.png";

const JewelleryTryOn = () => {

  const videoRef = useRef<null | HTMLVideoElement>(null)
  const canvasRef = useRef<null | HTMLCanvasElement>(null)

  const [webcamRunning, setWebcamRunning] = useState(false);
  const [faceLandmarker, setFaceLandmarker] = useState<null | FaceLandmarker>(null);
  const [poseLandmarker, setPoseLandmarker] = useState<null | PoseLandmarker>(null);
  const [faceLandmarks, setFaceLandmarks] = useState<Landmark[]>([]);
  const [poseLandmarks, setPoseLandmarks] = useState<Landmark[]>([]);
  const [videoDimensions, setVideoDimensions] = useState<{width: number, height: number}>({width: 1280, height: 720});
  const [ videoTexture, setVideoTexture] = useState<null | VideoTexture>(null);

  const predictWebcam = useCallback(async() => {
    if (!faceLandmarker || !poseLandmarker || !videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;


    let lastVideoTime = -1;

    const processFrame = async () => {
      if (lastVideoTime !== video.currentTime) {
        lastVideoTime = video.currentTime;
        const startTimeMs = performance.now();
        const faceLandmarkerResults = faceLandmarker.detectForVideo(video, startTimeMs);
        if (faceLandmarkerResults.faceLandmarks && faceLandmarkerResults.faceLandmarks.length > 0) {
          setFaceLandmarks(transformfaceLandmarks(faceLandmarkerResults.faceLandmarks[0]))
        }
        const poseLandmarkerResults = poseLandmarker.detectForVideo(video, startTimeMs);
        if (poseLandmarkerResults.landmarks && poseLandmarkerResults.landmarks.length > 0) {
          setPoseLandmarks(transformPoseLandmarks(poseLandmarkerResults.landmarks[0]))
        }
      }
      if (webcamRunning) {
        requestAnimationFrame(processFrame);
      }
    };

    processFrame();

  },[faceLandmarker, poseLandmarker, webcamRunning]);

  useEffect(() => {
    const loadModel = async () => {
      const filesetResolver = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.3/wasm"
      );
      const landmarker = await FaceLandmarker.createFromOptions(filesetResolver, {
        baseOptions: {
          modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task`,
          delegate: "GPU",
        },
        runningMode: "VIDEO",
        numFaces: 1,
      });
      setFaceLandmarker(landmarker);
    };
    loadModel();
  }, []);

  useEffect(()=>{
    const createPoseLandmarker = async () => {
      const vision = await FilesetResolver.forVisionTasks(
        "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm"
      );
      const poseLandmarker = await PoseLandmarker.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: `https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_lite/float16/1/pose_landmarker_lite.task`,
          delegate: "GPU"
        },
        runningMode: 'VIDEO',
        numPoses: 1
      });
      setPoseLandmarker(poseLandmarker);
    };
    createPoseLandmarker();
  },[])

  useEffect(() => {
    const enableWebcam = async () => {
      if (!videoRef.current) return;

      const constraints = {
        video: {
          aspectRatio: 16/9,
          frameRate: 60,
          width: { ideal: 2500 },
          height: { ideal: 1500 },
        },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      videoRef.current.srcObject = stream;

      videoRef.current.addEventListener("loadeddata", () => {
        if (videoRef.current) {
          setVideoDimensions({width: videoRef.current.videoWidth, height: videoRef.current.videoHeight})
          const texture = new VideoTexture(videoRef.current);
          setVideoTexture(texture);
        }
        predictWebcam();
      });
    };

    if (webcamRunning) {
      enableWebcam();
    }
  }, [predictWebcam, webcamRunning]);


  return (
    <div style={{ display: 'flex', flex: 1, justifyContent: 'center', alignItems: 'center', margin: 'auto', height: '100dvh'}}>
        <video
          ref={videoRef}
          autoPlay
          playsInline
          style={{
            display:"none",
          }}
        />
        {webcamRunning && (
          <Canvas
            ref={canvasRef}
            style={{
              width: `${window.innerWidth}px`,
              height: `${window.innerHeight}px`,
              transform: "rotateY(180deg)",
            }}
            dpr={window.devicePixelRatio || 1}
          >
            <OrthographicCamera
              makeDefault
              left={-videoDimensions.width / 2}
              right={videoDimensions.width / 2}
              top={videoDimensions.height / 2}
              bottom={-videoDimensions.height / 2}
              near={-2000}
              far={2000}
              position={[0, 0, 1]}
            />
            <mesh position={[0, 0, 0]}>
              <planeGeometry args={[videoDimensions.width, videoDimensions.height]} />
              {videoTexture && <meshBasicMaterial map={videoTexture} />}
            </mesh>
            {
              faceLandmarks.length && poseLandmarks.length &&
              <>
              <EarringMesh
                landmarks={faceLandmarks}
                // model={EARRING_MODEL_PATH}
                image={EARRING_IMAGE_PATH}
                videoWidth={videoDimensions.width}
                videoHeight={videoDimensions.height}
              />
              <NecklaceMesh
                landmarks={poseLandmarks}
                // model={NECKLACE_MODEL_PATH}
                image={NECKLACE_IMAGE_PATH}
                videoWidth={videoDimensions.width}
                videoHeight={videoDimensions.height}
              />
              </>
            }
            <ambientLight intensity={3} />
            <Environment preset="apartment" />
          </Canvas>
        )}
      <button onClick={() => setWebcamRunning(!webcamRunning)} style={{ display: webcamRunning ? 'none' : 'block' }}>
        Try on Jewellery
      </button>
    </div>
  )
}

export default JewelleryTryOn